import React, { useEffect, useState } from 'react';
import { 
  Button, 
  Table, 
  Modal, 
  Space, 
  Spin, 
  Card, 
  Alert, 
  Tag, 
  Tooltip, 
  Statistic,
  Row,
  Col,
  DatePicker,
  Select,
  Input
} from 'antd';
import { 
  TeamOutlined, 
  ReloadOutlined, 
  EyeOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import SummaryApi from '../common';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

// ✅ Inject CSS dynamically for modal styling
if (!document.getElementById('always-show-close-style')) {
  const style = document.createElement('style');
  style.id = 'always-show-close-style';
  style.innerHTML = `
    .always-show-close-btn .ant-btn-primary {
      opacity: 1 !important;
      visibility: visible !important;
      background-color: #1890ff !important;
      border-color: #1890ff !important;
      color: white !important;
    }
    .always-show-close-btn .ant-btn-primary:hover,
    .always-show-close-btn .ant-btn-primary:focus,
    .always-show-close-btn .ant-btn-primary:active {
      background-color: #40a9ff !important;
      border-color: #40a9ff !important;
      color: white !important;
    }
  `;
  document.head.appendChild(style);
}

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Search } = Input;

const CustomerAvailabilityReport = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    unavailable: 0,
    active: 0,
    inactive: 0
  });
  const [filters, setFilters] = useState({
    availability: 'all',
    status: 'all',
    search: '',
    dateRange: []
  });
  const navigate = useNavigate();

  // Authentication token from your configuration
  const AUTH_TOKEN = 'Bearer sri_danth_916';

  const fetchCustomers = async () => {
    setLoading(true);
    setApiError(null);
    try {
      console.log('🔄 Fetching customers...');
      
      // Check if API configuration exists
      if (!SummaryApi.allUser || !SummaryApi.allUser.url) {
        throw new Error('Customers API endpoint not configured in SummaryApi');
      }

      const response = await axios({
        method: SummaryApi.allUser.method || 'GET',
        url: SummaryApi.allUser.url,
        headers: {
          'Authorization': `Bearer ${AUTH_TOKEN}`,
          'Content-Type': 'application/json'
        },
        params: {
          action: 'get_customers',
          timestamp: Date.now() // Prevent caching
        },
        timeout: 30000
      });
      
      console.log('📊 API Response:', response.data);
      
      if (response.data) {
        let customersData = [];
        
        // Handle different response structures
        if (response.data.status === 'success' && response.data.data) {
          customersData = response.data.data;
        } else if (response.data.status === 'success' && Array.isArray(response.data)) {
          customersData = response.data;
        } else if (Array.isArray(response.data)) {
          customersData = response.data;
        } else if (response.data.customers && Array.isArray(response.data.customers)) {
          customersData = response.data.customers;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          customersData = response.data.data;
        } else {
          console.warn('Unexpected response structure:', response.data);
          customersData = [];
        }
        
        // Map to your tbl_customer structure
        const processedData = customersData.map(customer => ({
          customer_id: customer.customer_id || customer.id || '',
          full_name: customer.full_name || customer.name || 'Unknown Customer',
          mobile_number: customer.mobile_number || customer.phone || customer.mobile || '',
          email: customer.email || '',
          address: customer.address || '',
          latitude: customer.latitude || '0.0000',
          longitude: customer.longitude || '0.0000',
          is_available: customer.is_available || customer.available || 0,
          is_active: customer.is_active || customer.active || 1,
          created_date: customer.created_date || customer.created_at || customer.registration_date || ''
        }));
        
        console.log('📊 Processed customers data:', processedData);
        setCustomers(processedData);
        calculateStats(processedData);
        
        if (processedData.length > 0) {
          toast.success(`Loaded ${processedData.length} customers`);
        } else {
          toast.info('No customers found');
        }
      } else {
        throw new Error('Empty response from server');
      }
    } catch (error) {
      console.error('❌ Error fetching customers:', error);
      
      let errorMessage = 'API connection failed';
      if (error.response) {
        errorMessage = `Server Error: ${error.response.status}`;
        if (error.response.data) {
          try {
            const errorData = typeof error.response.data === 'string' 
              ? JSON.parse(error.response.data) 
              : error.response.data;
            errorMessage = `Server Error: ${error.response.status} - ${errorData.message || errorData.error || error.response.statusText}`;
          } catch (e) {
            errorMessage = `Server Error: ${error.response.status} - ${error.response.data}`;
          }
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check if the backend API is running.';
      } else if (error.message.includes('not configured')) {
        errorMessage = error.message;
      } else {
        errorMessage = `Network Error: ${error.message}`;
      }
      
      setApiError(errorMessage);
      toast.error('Failed to fetch customer data');
      
      // Set empty data
      setCustomers([]);
      calculateStats([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (customersData) => {
    const total = customersData.length;
    const available = customersData.filter(customer => 
      customer.is_available == 1 || customer.is_available === '1'
    ).length;
    const unavailable = customersData.filter(customer => 
      customer.is_available == 0 || customer.is_available === '0'
    ).length;
    const active = customersData.filter(customer => 
      customer.is_active == 1 || customer.is_active === '1'
    ).length;
    const inactive = customersData.filter(customer => 
      customer.is_active == 0 || customer.is_active === '0'
    ).length;
    
    setStats({ total, available, unavailable, active, inactive });
  };

  const getAvailabilityColor = (isAvailable) => {
    return isAvailable == 1 || isAvailable === '1' ? 'green' : 'red';
  };

  const getAvailabilityIcon = (isAvailable) => {
    return isAvailable == 1 || isAvailable === '1' ? <CheckOutlined /> : <CloseOutlined />;
  };

  const getAvailabilityText = (isAvailable) => {
    return isAvailable == 1 || isAvailable === '1' ? 'Available' : 'Not Available';
  };

  const getStatusColor = (isActive) => {
    return isActive == 1 || isActive === '1' ? 'blue' : 'default';
  };

  const getStatusText = (isActive) => {
    return isActive == 1 || isActive === '1' ? 'Active' : 'Inactive';
  };

  const getStatusIcon = (isActive) => {
    return isActive == 1 || isActive === '1' ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
  };

  const getLocationStatus = (latitude, longitude) => {
    if (!latitude || !longitude || latitude === '0.0000' || longitude === '0.0000') {
      return { color: 'orange', text: 'Location not set' };
    }
    return { color: 'green', text: 'Location available' };
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    return dayjs(date).fromNow();
  };

  const handleViewCustomerDetails = (customer) => {
    Modal.info({
      title: 'Customer Details',
      className: 'always-show-close-btn',
      content: (
        <div>
          {/* <p><strong>Customer ID:</strong> {customer.customer_id || 'N/A'}</p> */}
          <p><strong>Full Name:</strong> {customer.full_name || 'Unknown'}</p>
          <p><strong>Mobile Number:</strong> {customer.mobile_number || 'N/A'}</p>
          <p><strong>Email:</strong> {customer.email || 'N/A'}</p>
          <p><strong>Address:</strong> {customer.address || 'N/A'}</p>
          {customer.latitude && customer.latitude !== '0.0000' && (
            <p><strong>Location:</strong> {customer.latitude}, {customer.longitude}</p>
          )}
          <p><strong>Availability:</strong>{' '}
            <Tag color={getAvailabilityColor(customer.is_available)} className="ml-2">
              {getAvailabilityText(customer.is_available)}
            </Tag>
          </p>
          <p><strong>Account Status:</strong>{' '}
            <Tag color={getStatusColor(customer.is_active)} className="ml-2">
              {getStatusText(customer.is_active)}
            </Tag>
          </p>
          <p><strong>Registered:</strong> {customer.created_date ? dayjs(customer.created_date).format('DD MMM YYYY, hh:mm A') : 'N/A'}</p>
        </div>
      ),
      okText: 'Close',
    });
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesAvailability = filters.availability === 'all' || 
      (filters.availability === 'available' && (customer.is_available == 1 || customer.is_available === '1')) ||
      (filters.availability === 'unavailable' && (customer.is_available == 0 || customer.is_available === '0'));
    
    const matchesStatus = filters.status === 'all' || 
      (filters.status === 'active' && (customer.is_active == 1 || customer.is_active === '1')) ||
      (filters.status === 'inactive' && (customer.is_active == 0 || customer.is_active === '0'));
    
    const matchesSearch = !filters.search || 
      (customer.full_name && customer.full_name.toLowerCase().includes(filters.search.toLowerCase())) ||
      (customer.email && customer.email.toLowerCase().includes(filters.search.toLowerCase())) ||
      (customer.mobile_number && customer.mobile_number.includes(filters.search)) ||
      (customer.address && customer.address.toLowerCase().includes(filters.search.toLowerCase()));
    
    const matchesDate = !filters.dateRange.length || (
      dayjs(customer.created_date).isAfter(dayjs(filters.dateRange[0])) &&
      dayjs(customer.created_date).isBefore(dayjs(filters.dateRange[1]))
    );
    
    return matchesAvailability && matchesStatus && matchesSearch && matchesDate;
  });

  const columns = [
    {
      title: 'S.No.',
      key: 'sno',
      width: 80,
      render: (text, record, index) => <span className="font-mono text-sm">{index + 1}</span>
    },
    {
      title: 'Customer Details',
      key: 'customer',
      width: 280,
      render: (record) => (
        <div>
          <div className="font-semibold text-gray-900 flex items-center">
            <UserOutlined className="mr-2 text-blue-500" />
            {record.full_name || 'Unknown Customer'}
          </div>
          <div className="text-xs text-gray-500 mt-1 flex items-center">
            <MailOutlined className="mr-1" />
            {record.email || 'No email'}
          </div>
          <div className="text-xs text-gray-400 flex items-center">
            <PhoneOutlined className="mr-1" />
            {record.mobile_number || 'No phone'}
          </div>
          {record.address && (
            <div className="text-xs text-gray-400 flex items-center mt-1">
              <EnvironmentOutlined className="mr-1" />
              <span className="truncate">{record.address}</span>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Availability Status',
      key: 'availability',
      width: 150,
      render: (record) => (
        <div className="text-center">
          <Tag 
            color={getAvailabilityColor(record.is_available)} 
            icon={getAvailabilityIcon(record.is_available)}
            className="font-medium text-sm px-3 py-1"
          >
            {getAvailabilityText(record.is_available)}
          </Tag>
        </div>
      )
    },
    {
      title: 'Account Status',
      key: 'status',
      width: 120,
      render: (record) => (
        <div className="text-center">
          <Tag 
            color={getStatusColor(record.is_active)} 
            icon={getStatusIcon(record.is_active)}
            className="font-medium text-sm"
          >
            {getStatusText(record.is_active)}
          </Tag>
        </div>
      )
    },
    {
      title: 'Location',
      key: 'location',
      width: 150,
      render: (record) => {
        const locationStatus = getLocationStatus(record.latitude, record.longitude);
        return (
          <div className="text-center">
            <Tag color={locationStatus.color}>
              {locationStatus.text}
            </Tag>
            {(record.latitude && record.latitude !== '0.0000' && record.longitude && record.longitude !== '0.0000') && (
              <div className="text-xs text-gray-500 mt-1">
                {record.latitude}, {record.longitude}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Registration Date',
      dataIndex: 'created_date',
      key: 'created_date',
      width: 140,
      render: (date) => (
        <div className="text-center">
          <div className="font-medium text-gray-700">
            {date ? dayjs(date).format('DD MMM YYYY') : 'N/A'}
          </div>
          <div className="text-xs text-gray-500">
            {date ? getTimeAgo(date) : ''}
          </div>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (record) => (
        <Space size="small" direction="vertical">
          <Tooltip title="View Customer Details" placement="top">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewCustomerDetails(record)}
              style={{ width: '100%' }}
            >
              View Details
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchCustomers();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Customer Management</h2>
          <p className="text-gray-600">Manage customer availability and account status</p>
        </div>
        <div className="flex space-x-3">
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchCustomers}
            loading={loading}
            style={{ backgroundColor: '#1890ff' }}
          >
            Refresh Data
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {/* <Row gutter={16}>
        <Col span={4}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #1890ff 0%, #70c1ff 100%)',
              color: '#fff',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: '#fff' }}>Total Customers</span>}
              value={stats.total}
              prefix={<TeamOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>

        <Col span={4}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #52c41a 0%, #b7eb8f 100%)',
              color: '#fff',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: '#fff' }}>Available</span>}
              value={stats.available}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>

        <Col span={4}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
              color: '#fff',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: '#fff' }}>Not Available</span>}
              value={stats.unavailable}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>

        <Col span={4}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
              color: '#fff',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: '#fff' }}>Active Accounts</span>}
              value={stats.active}
              prefix={<CheckCircleOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>

        <Col span={4}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
              color: '#fff',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: '#fff' }}>Inactive Accounts</span>}
              value={stats.inactive}
              prefix={<CloseCircleOutlined style={{ color: '#fff' }} />}
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>

        <Col span={4}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #389e0d 0%, #73d13d 100%)',
              color: '#fff',
              border: 'none',
            }}
          >
            <Statistic
              title={<span style={{ color: '#fff' }}>Availability Rate</span>}
              value={stats.total > 0 ? Math.round((stats.available / stats.total) * 100) : 0}
              suffix="%"
              valueStyle={{ color: '#fff' }}
            />
          </Card>
        </Col>
      </Row> */}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-gray-700 font-medium">Availability:</span>
            <Select
              value={filters.availability}
              onChange={(value) => setFilters(prev => ({ ...prev, availability: value }))}
              style={{ width: 140 }}
            >
              <Option value="all">All</Option>
              <Option value="available">Available</Option>
              <Option value="unavailable">Not Available</Option>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-gray-700 font-medium">Status:</span>
            <Select
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              style={{ width: 120 }}
            >
              <Option value="all">All</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2 flex-1 min-w-64">
            <Search
              placeholder="Search by name, email, phone, or address..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onSearch={fetchCustomers}
              style={{ width: 300 }}
              allowClear
              enterButton={
                <button
                  className="ant-input-search-button"
                  style={{ backgroundColor: '#1890ff', border: 'none', display: 'flex', alignItems: 'center' }}
                >
                  <SearchOutlined style={{ color: '#fff' }} />
                </button>
              }
            />
          </div>
        </div>

        {apiError && (
          <Alert
            message="API Connection Issue"
            description={apiError}
            type="warning"
            showIcon
            closable
            action={
              <Button size="small" onClick={fetchCustomers}>
                Retry
              </Button>
            }
          />
        )}

        <div className="flex justify-between items-center mb-4">
          <div className="text-gray-600 font-medium">
            Showing {filteredCustomers.length} of {customers.length} customers
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Spin size="large" />
            <p className="mt-3 text-gray-500">Loading customer data...</p>
          </div>
        ) : (
          <Table 
            rowKey="customer_id" 
            dataSource={filteredCustomers} 
            columns={columns}
            scroll={{ x: 1500 }}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} customers`
            }}
            locale={{
              emptyText: (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4" style={{ fontSize: '48px' }}>
                    <TeamOutlined />
                  </div>
                  <p className="text-gray-500 text-lg mb-2">No customer data found</p>
                  <p className="text-gray-400 text-sm mb-4">
                    {filters.availability !== 'all' || filters.status !== 'all' || filters.search || filters.dateRange.length 
                      ? 'Try adjusting your filters' 
                      : 'No customer records available'
                    }
                  </p>
                </div>
              )
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default CustomerAvailabilityReport;